/**
 * Cart Oatmilk Handler
 * 
 * This module manages the automatic removal of free oatmilk items when their
 * associated Essential Subscription Box is removed from the cart.
 * 
 * @module CartOatmilkHandler
 */

(function () {
  'use strict';

  /**
   * Configuration object for cart item identification
   */
  const CONFIG = {
    essentialBoxSelector: '.item:has(img[alt*="Essential - Subscription Box"])',
    oatmilkSelector: '.item:has(img[alt*="Oatmilk"])',
    removeButtonSelector: '.remove',
    cartItemsContainer: '#slidecarthq .items'
  };

  /**
   * CartOatmilkHandler class
   * Handles the dependency between Essential Subscription Box and free oatmilk
   */
  class CartOatmilkHandler {
    constructor() {
      this.cartContainer = null;
      this.isRemoving = false;
      this.init();
    }

    /**
     * Initialize the handler
     * Sets up observers and event listeners
     */
    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    /**
     * Setup the mutation observer and initial event listeners
     */
    setup() {
      this.cartContainer = document.querySelector(CONFIG.cartItemsContainer);
      
      if (!this.cartContainer) {
        // If cart container doesn't exist yet, wait and retry
        setTimeout(() => this.setup(), 500);
        return;
      }

      // Attach event listeners to existing items
      this.attachEventListeners();

      // Observe for dynamically added cart items
      this.observeCartChanges();
    }

    /**
     * Attach click event listeners to Essential Box remove buttons
     */
    attachEventListeners() {
      const essentialBoxItems = document.querySelectorAll(CONFIG.essentialBoxSelector);
      
      essentialBoxItems.forEach(item => {
        const removeButton = item.querySelector(CONFIG.removeButtonSelector);
        
        if (removeButton && !removeButton.dataset.oatmilkHandlerAttached) {
          removeButton.addEventListener('click', (e) => this.handleEssentialBoxRemoval(e));
          // Mark as attached to prevent duplicate listeners
          removeButton.dataset.oatmilkHandlerAttached = 'true';
        }
      });
    }

    /**
     * Handle the removal of Essential Subscription Box
     * Automatically removes associated oatmilk item
     * 
     * @param {Event} event - The click event
     */
    handleEssentialBoxRemoval(event) {
      // Prevent multiple simultaneous removals
      if (this.isRemoving) {
        return;
      }

      this.isRemoving = true;

      // Get the oatmilk item key BEFORE the Essential box is removed
      const oatmilkItem = document.querySelector(CONFIG.oatmilkSelector);
      
      if (oatmilkItem) {
        const itemKey = oatmilkItem.dataset.lineItemKey;
        
        if (itemKey) {
          // Wait for the Essential box to be removed from the cart
          setTimeout(async () => {
            await this.removeOatmilkItem(itemKey);
            this.isRemoving = false;
          }, 500);
        } else {
          this.isRemoving = false;
        }
      } else {
        this.isRemoving = false;
      }
    }

    /**
     * Remove the oatmilk item from the cart using Shopify Cart API
     * 
     * @param {string} itemKey - The line item key from data-line-item-key
     */
    async removeOatmilkItem(itemKey) {
      try {
        // Use Shopify Cart API to remove the item by setting quantity to 0
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: itemKey,
            quantity: 0,
          }),
        });

        if (!response.ok) {
          console.error('Failed to remove oatmilk item:', response.statusText);
          return;
        }

        const cartData = await response.json();

        // Trigger cart refresh using multiple methods to ensure compatibility
        this.refreshCart(cartData);
      } catch (error) {
        console.error('Error removing oatmilk item:', error);
      }
    }

    /**
     * Refresh the cart UI after removing oatmilk
     * Uses multiple methods to ensure compatibility with different cart systems
     * 
     * @param {Object} cartData - The updated cart data from Shopify
     */
    refreshCart(cartData) {
      // Method 1: Dispatch cart:refresh event (most common for third-party carts)
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        detail: { cartData },
        bubbles: true
      }));

      // Method 2: Alternative cart update event
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cartData },
        bubbles: true
      }));

      // Method 3: Trigger SlideCartHQ refresh if available
      if (window.SlideCartHQ && typeof window.SlideCartHQ.refresh === 'function') {
        window.SlideCartHQ.refresh();
      }

      // Method 4: Force re-render by simulating a cart change event
      if (window.jQuery && window.jQuery.ajax) {
        window.jQuery(document.body).trigger('added.ajaxCart');
      }

      // Method 5: Publish to PubSub system if available
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'oatmilk-handler',
          cartData: cartData
        });
      }

      // Method 6: Manually remove the DOM element as a fallback
      this.removeOatmilkFromDOM();
    }

    /**
     * Manually remove the oatmilk DOM element from the cart
     * This is a fallback in case the cart doesn't automatically refresh
     */
    removeOatmilkFromDOM() {
      const oatmilkItem = document.querySelector(CONFIG.oatmilkSelector);
      if (oatmilkItem) {
        // Fade out animation before removing
        oatmilkItem.style.transition = 'opacity 0.3s ease-out';
        oatmilkItem.style.opacity = '0';
        
        setTimeout(() => {
          oatmilkItem.remove();
        }, 300);
      }
    }

    /**
     * Observe cart container for changes
     * Re-attach event listeners when cart is updated
     */
    observeCartChanges() {
      const observer = new MutationObserver((mutations) => {
        // Check if new items were added
        const hasNewItems = mutations.some(mutation => 
          mutation.addedNodes.length > 0
        );

        if (hasNewItems) {
          // Re-attach event listeners to any new Essential Box items
          this.attachEventListeners();
        }
      });

      observer.observe(this.cartContainer, {
        childList: true,
        subtree: true
      });
    }
  }

  // Initialize the handler
  new CartOatmilkHandler();
})();

